import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { PlaylistTrack } from '@app/core/models/Track';
import {
  convertToPlaylist,
  convertToPlaylists,
  PlaylistApi,
} from '@app/library/models/playlist-api.model';
import { Playlist } from '@app/library/models/playlist.model';
import { environment } from '@env/environment';
import moment from 'moment-timezone';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';

import { PlaylistTrackApiModel } from './track.service';

const playlistURL = {
  getRadioPlaylist: (id: number) =>
    `${environment.urls.MAIN_API_V2}/radio/${id}/playlist`,
  createPlaylist: (id: number) => `${environment.urls.MAIN_API_V2}/radio/${id}/playlist`,
  getPlaylist: (id: number, idPlaylist: number) =>
    `${environment.urls.MAIN_API_V2}/radio/${id}/playlist/${idPlaylist}`,
  editPlaylist: (id: number, idPlaylist: number) =>
    `${environment.urls.MAIN_API_V2}/radio/${id}/playlist/${idPlaylist}`,
  deletePlaylist: (id: number, idPlaylist: number) =>
    `${environment.urls.MAIN_API_V2}/radio/${id}/playlist/${idPlaylist}`,
  duplicatePlaylist: (id: number, idPlaylist: number) =>
    `${environment.urls.MAIN_API_V2}/radio/${id}/playlist/${idPlaylist}/duplicate`,
  addToPlaylist: (id: number, idPlaylist: number) =>
    `${environment.urls.MAIN_API_V2}/radio/${id}/playlist/${idPlaylist}/tracks`,
  forceAddToPlaylist: (id: number, idPlaylist: number, force: string) =>
    `${environment.urls.MAIN_API_V2}/radio/${id}/playlist/${idPlaylist}/tracks?force=${force}`,
  getPlaylistTracks: (id: number, idPlaylist: number) =>
    `${environment.urls.MAIN_API_V2}/radio/${id}/playlist/${idPlaylist}/tracks`,
  movePlaylistTracks: (id: number, idPlaylist: number) =>
    `${environment.urls.MAIN_API_V2}/radio/${id}/playlist/${idPlaylist}/tracks`,
  deleteTracksInPlaylist: (id: number, idPlaylist: number) =>
    `${environment.urls.MAIN_API_V2}/radio/${id}/playlist/${idPlaylist}/tracks`,
};

@Injectable({
  providedIn: 'root',
})
export class PlaylistService {
  constructor(private readonly httpClient: HttpClient) {}

  getRadioPlaylists(idRadio: number): Observable<Playlist[]> {
    return this.httpClient
      .get<PlaylistApi[]>(playlistURL.getRadioPlaylist(idRadio))
      .pipe(map(data => convertToPlaylists(data)));
  }

  getPlaylistDetails(idRadio: number, idPlaylist: number): Observable<Playlist> {
    return this.httpClient
      .get<PlaylistApi>(playlistURL.getPlaylist(idRadio, idPlaylist))
      .pipe(map(playlist => convertToPlaylist(playlist)));
  }

  createPlaylist(idRadio: number, playlist: Partial<PlaylistApi>): Observable<Playlist> {
    return this.httpClient
      .post<PlaylistApi>(playlistURL.createPlaylist(idRadio), playlist)
      .pipe(map(data => convertToPlaylist(data)));
  }

  editPlaylist(
    idRadio: number,
    idPlaylist: number,
    playlist: Partial<Playlist>,
  ): Observable<Playlist> {
    return this.httpClient
      .put<PlaylistApi>(playlistURL.editPlaylist(idRadio, idPlaylist), playlist)
      .pipe(map(data => convertToPlaylist(data)));
  }

  addToPlaylist(
    idRadio: number,
    idPlaylist: number,
    tracks: number[],
    force?: string,
    position?: number,
  ): Observable<PlaylistTrack[]> {
    const payload = {
      tracks,
    };
    if (position) {
      payload['position'] = position;
    }
    if (force) {
      return this.httpClient
        .post<PlaylistTrackApiModel[]>(
          playlistURL.forceAddToPlaylist(idRadio, idPlaylist, force),
          payload,
        )
        .pipe(map(data => this.convertMultipleToTrack(data, idPlaylist)));
    }

    return this.httpClient
      .post<PlaylistTrackApiModel[]>(
        playlistURL.addToPlaylist(idRadio, idPlaylist),
        payload,
      )
      .pipe(map(data => this.convertMultipleToTrack(data, idPlaylist)));
  }

  getPlaylistTracks(idRadio: number, idPlaylist: number): Observable<PlaylistTrack[]> {
    return this.httpClient
      .get<PlaylistTrackApiModel[]>(playlistURL.getPlaylistTracks(idRadio, idPlaylist))
      .pipe(map(tracks => this.convertMultipleToTrack(tracks, idPlaylist)));
  }

  movePlaylistTracks(
    idRadio: number,
    idPlaylist: number,
    position: number,
    tracks: number[],
  ): Observable<PlaylistTrack[]> {
    return this.httpClient
      .put<PlaylistTrackApiModel[]>(playlistURL.movePlaylistTracks(idRadio, idPlaylist), {
        position,
        tracks,
      })
      .pipe(map(data => this.convertMultipleToTrack(data, idPlaylist)));
  }

  deletePlaylistTracks(
    idRadio: number,
    idPlaylist: number,
    tracks: number[],
  ): Observable<PlaylistTrack[]> {
    return this.httpClient
      .request<PlaylistTrackApiModel[]>(
        'DELETE',
        playlistURL.deleteTracksInPlaylist(idRadio, idPlaylist),
        {
          body: {
            tracks,
          },
        },
      )
      .pipe(map(data => this.convertMultipleToTrack(data, idPlaylist)));
  }

  deletePlaylist(idRadio: number, idPlaylist: number): Observable<any> {
    return this.httpClient.delete(playlistURL.deletePlaylist(idRadio, idPlaylist));
  }

  duplicatePlaylist(idRadio: number, playlist: Partial<Playlist>): Observable<Playlist> {
    return this.httpClient
      .post<PlaylistApi>(playlistURL.duplicatePlaylist(idRadio, playlist.id), {
        name: playlist.name,
        color: playlist.color,
      })
      .pipe(map(data => convertToPlaylist(data)));
  }

  convertMultipleToTrack(
    apiTrack: PlaylistTrackApiModel[],
    idPlaylist: number,
  ): PlaylistTrack[] {
    return apiTrack.map(data => this.convertToTrack(data, idPlaylist));
  }

  convertToTrack(track: PlaylistTrackApiModel, idPlaylist: number): PlaylistTrack {
    return {
      idPlaylist,
      position: track.position,
      idTrackbox: track.idbox,
      id: track.idtrack,
      title: track.title || '',
      artist: track.artist || '',
      album: track.album || '',
      cover: track.cover_url || 'assets/placeholder-square.png',
      isCustomCover: track.is_custom_cover,
      file: track.audio_url,
      year: track.year,
      buyLink: track.buy_link,
      bpm: track.bpm,
      uploadDate: moment(track.upload_date),
      bitrate: track.bitrate,
      timeSeconds: track.playtime,
      locked: track.locked,
      tags:
        track.tags?.map(data => ({
          id: data.idtag,
          name: data.name,
          color: data.color,
        })) ?? [],
      playlists:
        track.playlists?.map(data => ({
          id: data.idplaylist,
          name: data.name,
          color: data.color,
          type: data.type,
        })) ?? [],
    };
  }
}
